import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChevronRight, AlertTriangle, AlertCircle, Bell, Clock } from "lucide-react";

const BASE = process.env.REACT_APP_API_URL || "";

async function fetchAlerts() {
  const res = await fetch(`${BASE}/churn-prediction/alerts?status=active&limit=10`, {
    credentials: 'include', // Use httpOnly cookie
  });
  if (!res.ok) throw new Error("Failed to fetch alerts");
  const json = await res.json();
  return { alerts: json.alerts || [], counts: json.counts || {} };
}

// Alert type styling and icons
const alertTypeConfig = {
  emergency: {
    bg: "bg-red-100",
    text: "text-red-800",
    border: "border-l-4 border-red-600",
    badge: "bg-red-600 text-white",
    icon: AlertTriangle,
    label: "EMERGENCY"
  },
  urgent: {
    bg: "bg-orange-100",
    text: "text-orange-800",
    border: "border-l-4 border-orange-500",
    badge: "bg-orange-500 text-white",
    icon: AlertCircle,
    label: "URGENT"
  },
  warning: {
    bg: "bg-yellow-100",
    text: "text-yellow-800",
    border: "border-l-4 border-yellow-500",
    badge: "bg-yellow-500 text-white",
    icon: Bell,
    label: "WARNING"
  }
};

function formatTimeRemaining(dueAt) {
  if (!dueAt) return null;
  const due = new Date(dueAt);
  const now = new Date();
  const diffMs = due - now;

  if (diffMs < 0) return "OVERDUE";

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 24) return `${hours}h remaining`;
  const days = Math.floor(hours / 24);
  return `${days}d remaining`;
}

const AlertsWidget = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const { alerts: data, counts: countData } = await fetchAlerts();
        setAlerts(data);
        setCounts(countData);
      } catch (err) {
        console.error("Failed to load alerts:", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Calculate total active alerts
  const totalAlerts = Object.values(counts).reduce((sum, c) => sum + (c || 0), 0);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow p-6 border-t-4" style={{ borderTopColor: "#FFB800" }}>
        <div className="text-lg font-semibold text-[#172A3A] mb-4">{t('alerts')}</div>
        <div className="animate-pulse space-y-2">
          {[1, 2].map(i => (
            <div key={i} className="h-16 bg-gray-100 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow p-6 border-t-4" style={{ borderTopColor: "#FFB800" }}>
      <div
        className="flex items-center justify-between mb-4 cursor-pointer hover:opacity-80 transition-opacity"
        onClick={() => navigate('/risk?tab=alerts')}
      >
        <div className="flex items-center gap-2">
          <div className="text-lg font-semibold text-[#172A3A]">{t('alerts')}</div>
          {totalAlerts > 0 && (
            <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-600 text-white">
              {totalAlerts}
            </span>
          )}
        </div>
        <ChevronRight className="text-gray-400" size={20} />
      </div>

      {/* Alert type counts */}
      {totalAlerts > 0 && (
        <div className="flex gap-2 mb-3">
          {counts.emergency > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">
              {counts.emergency} Emergency
            </span>
          )}
          {counts.urgent > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-orange-100 text-orange-800">
              {counts.urgent} Urgent
            </span>
          )}
          {counts.warning > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-800">
              {counts.warning} Warning
            </span>
          )}
        </div>
      )}

      {alerts.length === 0 ? (
        <div className="text-gray-500 text-sm py-4 text-center">
          <Bell className="mx-auto mb-2 text-gray-300" size={24} />
          {t('noAlerts') || 'No active alerts'}
        </div>
      ) : (
        <ul className="space-y-2 max-h-64 overflow-y-auto">
          {alerts.slice(0, 5).map((alert) => {
            const config = alertTypeConfig[alert.alert_type] || alertTypeConfig.warning;
            const IconComponent = config.icon;
            const clientName = `${alert.first_name || ''} ${alert.last_name || ''}`.trim() || 'Unknown';
            const timeRemaining = formatTimeRemaining(alert.response_due_at);

            return (
              <li
                key={alert.id}
                className={`px-3 py-2 rounded cursor-pointer hover:opacity-90 transition-opacity ${config.bg} ${config.text} ${config.border}`}
                onClick={() => navigate(`/clients/${alert.client_id}`)}
              >
                <div className="flex items-start gap-2">
                  <IconComponent size={16} className="mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${config.badge}`}>
                        {config.label}
                      </span>
                      <span className="font-semibold text-sm truncate">{clientName}</span>
                    </div>
                    <p className="text-xs mt-1 line-clamp-2">{alert.alert_title || alert.alert_message}</p>
                    {timeRemaining && (
                      <div className="flex items-center gap-1 mt-1 text-xs opacity-75">
                        <Clock size={10} />
                        <span className={timeRemaining === 'OVERDUE' ? 'font-bold text-red-700' : ''}>
                          {timeRemaining}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {alerts.length > 5 && (
        <button
          onClick={() => navigate('/risk?tab=alerts')}
          className="w-full mt-3 text-sm text-[#FFB800] hover:text-[#E5A600] font-medium"
        >
          View all {totalAlerts} alerts
        </button>
      )}
    </div>
  );
};

export default AlertsWidget;
